import { Fragment, useId, type ReactNode } from 'react'
import { useEinblenden } from '@/hooks/useEinblenden'

/** Offizielles FOM-Logo mit Claim „Die Hochschule. Für Berufstätige.“ */
export function FomLogo({ invers = true, className = 'h-10' }: Readonly<{ invers?: boolean; className?: string }>) {
  return (
    <img
      src={invers ? '/fom-logo-invers.png' : '/fom-logo-color.png'}
      alt="FOM Hochschule – Die Hochschule. Für Berufstätige."
      className={`${className} w-auto`}
      width={900}
      height={282}
    />
  )
}

/** Dunkles Etikett mit gesperrter Schrift und Linie nach rechts - wie „DISKUSSIONSTHEMEN“ auf der Karte. */
export function Etikett({ children, aufDunkel = false }: Readonly<{ children: ReactNode; aufDunkel?: boolean }>) {
  return (
    <div className="flex items-center">
      <span className="bg-konf-nacht px-4 py-2 font-display text-[12.5px] font-medium uppercase tracking-[0.34em] text-konf-hell sm:px-5 sm:text-[13.5px]">
        {children}
      </span>
      <span aria-hidden="true" className={`h-px flex-1 ${aufDunkel ? 'bg-white/30' : 'bg-konf-nacht/35'}`} />
    </div>
  )
}

/** Blendet den Inhalt ein, sobald er ins Bild kommt. */
export function Einblenden({ children, verzoegerung = 0, className = '' }: Readonly<{ children: ReactNode; verzoegerung?: number; className?: string }>) {
  const ref = useEinblenden<HTMLDivElement>(verzoegerung)
  return <div ref={ref} className={`einblenden ${className}`}>{children}</div>
}

// Spitzes Sechseck, Seitenlaenge 24: Breite 24*sqrt(3), Kachelhoehe 72 (eine Reihe + Versatz)
const B = 41.569
const WABE = `M${B / 2} 0L${B} 12L${B} 36L${B / 2} 48L0 36L0 12Z M${B / 2} 48L${B / 2} 72`

/** Wabennetz der Karte: eine perspektivisch geneigte Flaeche, die langsam heranfaehrt. */
export function Wabennetz({ className = 'h-[55%]', staerke = 0.42 }: Readonly<{ className?: string; staerke?: number }>) {
  const id = 'wabe' + useId().replace(/:/g, '')
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 -z-10 overflow-hidden ${className}`} style={{ perspective: '640px' }}>
      <div
        className="absolute bottom-[-35%] left-[-70%] right-[-70%] h-[170%]"
        style={{
          transform: 'rotateX(60deg)',
          transformOrigin: '50% 100%',
          maskImage: 'linear-gradient(to top, #000 18%, transparent 82%)',
          WebkitMaskImage: 'linear-gradient(to top, #000 18%, transparent 82%)',
        }}
      >
        <svg className="wabe-fahrt absolute inset-x-0 top-[-72px] h-[calc(100%+72px)] w-full">
          <defs>
            <pattern id={id} width={B} height={72} patternUnits="userSpaceOnUse">
              <path d={WABE} fill="none" stroke={`rgba(200,245,238,${staerke})`} strokeWidth={1.1} />
              <circle cx={B / 2} cy={48} r={1.5} fill={`rgba(220,255,250,${staerke * 1.6})`} />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#${id})`} />
        </svg>
      </div>
    </div>
  )
}

/** Schwebende Lichtpunkte wie auf der Karte (rein dekorativ). */
const FUNKEN = [
  [62, 18, 3], [71, 24, 2], [83, 12, 2], [91, 30, 3], [55, 34, 2], [77, 38, 2], [88, 46, 2],
  [12, 22, 2], [24, 40, 2], [38, 15, 2], [66, 50, 3], [95, 20, 2], [48, 26, 2], [80, 58, 2],
]
export function Funken() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      {FUNKEN.map(([x, y, r], i) => (
        <span
          key={i}
          className="funkeln absolute rounded-full bg-[#BFF5EC]"
          style={{ left: `${x}%`, top: `${y}%`, width: r, height: r, animationDelay: `${(i * 0.7) % 4.5}s` }}
        />
      ))}
    </div>
  )
}

/** **fett** -> <strong>, [Text](Adresse) -> Verweis */
export function MitFett({ text, hell = false }: Readonly<{ text: string; hell?: boolean }>) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*|\[[^[\]]+\]\([^()]+\))/).map((teil, i) => {
        const fett = /^\*\*([^*]+)\*\*$/.exec(teil)
        if (fett) return <strong key={i} className={`font-display font-bold ${hell ? 'text-white' : 'text-konf-nacht'}`}>{fett[1]}</strong>
        const link = /^\[([^[\]]+)\]\(([^()]+)\)$/.exec(teil)
        if (link) {
          return (
            <a key={i} href={link[2]} target="_blank" rel="noopener" className={`underline decoration-1 underline-offset-[3px] transition-colors ${hell ? 'text-white decoration-konf-hell hover:text-konf-hell' : 'text-konf-teal hover:text-konf-nacht'}`}>
              {link[1]}
            </a>
          )
        }
        return <Fragment key={i}>{teil}</Fragment>
      })}
    </>
  )
}
